import type React from 'react'
import type { ResultPill } from '../../components/v2/pv2-shared'
import { deleteSessionResult } from '../../lib/api'
// PRI-496 Lane B: annotated playback rides public Analysis.artifacts
// (signed URL, no token) for an_ ids; legacy UUID history rows keep the
// internal token-query URL until they age out.
import { getAnnotatedVideoUrl, isPublicAnalysisId } from '../../core/lib/api/public-inference'
import { triggerZipDownload } from '../../lib/export'
import {
  trackDownloadStarted,
  trackDownloadCompleted,
  trackDownloadFailed,
  trackUpgradeClicked,
  trackResultPillDeleted,
} from '../../lib/analytics'

/**
 * useResultPillHandlers — Phase 5 (PRI-291)
 *
 * Extracts result-pill–related handler functions from PrimateVisionV2Inner:
 *   - handleDownloadPill
 *   - handleDeleteResult
 *   - attachRenderedVideo
 *   - attachRenderedVideoForced
 *   - attachStreamRecordingUrl
 *   - attachStreamRecordingUrlForced
 *   - handleRerunStream
 *
 * The hook receives `{ fetchToken }` from useAuthToken plus needed state/setters.
 */
export function useResultPillHandlers({
  fetchToken,
  setResultPills,
  canDownload,
  setShowUpgradeForDownload,
  planTier,
  setDownloadingPillIds,
  setPrompt,
  setIsStreaming,
  lastStreamResultRef,
  requestCameraAccess,
}: {
  fetchToken: () => Promise<string | null>
  setResultPills: React.Dispatch<React.SetStateAction<ResultPill[]>>
  canDownload: boolean
  setShowUpgradeForDownload: (v: boolean) => void
  planTier: string
  setDownloadingPillIds: React.Dispatch<React.SetStateAction<Set<string>>>
  setPrompt: (p: string) => void
  setIsStreaming: (v: boolean) => void
  lastStreamResultRef: React.MutableRefObject<unknown>
  requestCameraAccess: () => Promise<void>
}) {
  // ── attachRenderedVideo ───────────────────────────────────────────────────
  // Fetch the rendered output video URL and update the pill.
  // an_ ids: signed artifacts URL (no token — safe for <video src>).
  // Legacy UUIDs: internal URL + auth token as query param (<video> elements
  // cannot set headers).
  async function resolvePlaybackUrl(jobId: string): Promise<string | null> {
    const baseUrl = await getAnnotatedVideoUrl(jobId)
    if (!baseUrl) return null
    if (isPublicAnalysisId(jobId)) return baseUrl // already signed
    const token = await fetchToken()
    return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl
  }

  async function attachRenderedVideo(jobId: string) {
    try {
      const videoUrl = await resolvePlaybackUrl(jobId)
      if (!videoUrl) return
      const headRes = await fetch(videoUrl, { method: 'HEAD' }).catch(() => null)
      if (headRes && headRes.ok) {
        setResultPills(prev => prev.map(p => p.id === jobId ? { ...p, videoUrl } : p))
      }
    } catch { /* non-fatal */ }
  }

  // ── attachRenderedVideoForced ─────────────────────────────────────────────
  // Force-attach video URL on user demand (skips HEAD check).
  async function attachRenderedVideoForced(jobId: string) {
    try {
      const videoUrl = await resolvePlaybackUrl(jobId)
      if (!videoUrl) return
      setResultPills(prev => prev.map(p => p.id === jobId ? { ...p, videoUrl } : p))
    } catch { /* non-fatal */ }
  }

  // ── attachStreamRecordingUrl ──────────────────────────────────────────────
  async function attachStreamRecordingUrl(pillId: string, recordingUrl: string) {
    try {
      const token = await fetchToken()
      const urlWithToken = token ? `${recordingUrl}&token=${encodeURIComponent(token)}` : recordingUrl
      setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, videoUrl: urlWithToken } : p))
    } catch { /* non-fatal */ }
  }

  // ── attachStreamRecordingUrlForced ────────────────────────────────────────
  async function attachStreamRecordingUrlForced(pillId: string, recordingUrl: string) {
    const token = await fetchToken()
    const urlWithToken = token ? `${recordingUrl}&token=${encodeURIComponent(token)}` : recordingUrl
    setResultPills(prev => prev.map(p => p.id === pillId ? { ...p, videoUrl: urlWithToken } : p))
  }

  // ── handleRerunStream ─────────────────────────────────────────────────────
  function handleRerunStream(promptText: string) {
    setPrompt(promptText)
    setIsStreaming(true)
    lastStreamResultRef.current = null
    void requestCameraAccess()
  }

  // ── handleDownloadPill ────────────────────────────────────────────────────
  async function handleDownloadPill(pill: ResultPill) {
    if (!canDownload) {
      setShowUpgradeForDownload(true)
      trackUpgradeClicked('download_gate')
      return
    }
    const pillType = pill.id.startsWith('stream-') ? 'stream' : 'upload'
    setDownloadingPillIds(prev => new Set(prev).add(pill.id))
    trackDownloadStarted({ pill_type: pillType, user_plan: planTier })
    try {
      const token = await fetchToken()
      await triggerZipDownload(pill, token)
      trackDownloadCompleted({
        pill_type: pillType, user_plan: planTier,
        has_video: !!pill.videoUrl || !!pill.recordingUrl,
      })
    } catch (err) {
      console.error('[Download] ZIP generation failed:', err)
      trackDownloadFailed({ pill_type: pillType, error: String(err), user_plan: planTier })
    } finally {
      setDownloadingPillIds(prev => { const next = new Set(prev); next.delete(pill.id); return next })
    }
  }

  // ── handleDeleteResult ────────────────────────────────────────────────────
  async function handleDeleteResult(pill: ResultPill) {
    const pillType = pill.id.startsWith('stream-') ? 'stream' : 'upload'
    try {
      trackResultPillDeleted({ pill_type: pillType, user_plan: planTier })
    } catch { /* analytics best-effort */ }
    const { sessionResultId, userVideoId } = pill
    if (!sessionResultId || !userVideoId) {
      setResultPills(prev => prev.filter(p => p.id !== pill.id))
      return
    }
    try {
      await deleteSessionResult(userVideoId, sessionResultId)
    } catch { /* non-fatal - still remove from UI */ }
    setResultPills(prev => prev.filter(p => p.id !== pill.id))
  }

  return {
    attachRenderedVideo,
    attachRenderedVideoForced,
    attachStreamRecordingUrl,
    attachStreamRecordingUrlForced,
    handleRerunStream,
    handleDownloadPill,
    handleDeleteResult,
  }
}
